import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Customers } from '../../../account-info-header/models/AccountInfoApiResponse';
import { selectEAuthCustomers } from '../../../account-info-header/reducers';
import { CodeAndDescription } from '../../../create-request/models/shared/CodeAndDescription';
import { FormatDatePipe } from '../../../shared/pipes/FormatDatePipe';
import { FormatTimePipe } from '../../../shared/pipes/FormatTimePipe';
import { NamePipe } from '../../../shared/pipes/NamePipe';
import {
  eAuthStatusStrings,
  pendReasonsStrings,
  sourceOfLastModification,
  transactionTypes
} from '../../../shared/utilities/Constants';
import { ViewDetails } from '../../models/ViewDetails';
import { EAuthStatusMessageFields } from '../../../create-request/models/internal-transfer/InternalTransferInquiryResponse';
import { DatePipe } from '@angular/common';
import { selectHasAccessInitiateMoneyMovement } from '../../../authorize/reducers';
import { convertUTCToEasternTime24 } from '../../../shared/utilities/datetime-utils';
import { selectStagedTransactionForm } from '../../../create-request/reducers';
import { ChecksExtraPrompts } from '../../../create-request/models/form/TransactionForm';

@Component({
  standalone: false,
  selector: 'mm-view-details',
  templateUrl: './view-details.component.html',
  styleUrls: ['./view-details.component.scss']
})
export class ViewDetailsComponent implements OnInit {
  @Input() viewDetails$: Observable<ViewDetails>;
  @Input() pendReasons$: Observable<string[]>;
  @Input() useCondensedLayout: boolean;
  readonly pendReasonStrings = pendReasonsStrings;
  destroy$: Subject<boolean> = new Subject<boolean>();
  caseId: string;
  status: string;
  transactionType: string;
  submittedOn: string;
  source: string;
  initiatedBy: string;
  releasedDate: string;
  releasedBy: string;
  formatDatePipe = new FormatDatePipe();
  lastModificationSource: string;
  lastModifiedBy: string;
  lastModified: string;
  isLastModified: boolean;
  formatTimePipe = new FormatTimePipe();
  pendReasons: string[];
  isPendingAuthorization: boolean;
  namePipe = new NamePipe();
  canDisplayLastModifiedBy: boolean;
  eAuthCustomers: Customers[];
  selectedEauthCustomers: Customers[] = [];
  accountselected: EAuthStatusMessageFields[] | undefined = []
  statusDescription: string;
  hasMmInitiateAccess: boolean;
  isCheckBsloa: boolean = false;

  constructor(private store: Store, private datePipe: DatePipe) { }

  ngOnInit(): void {
    this.pendReasons$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(pendReasons => {
      this.pendReasons = pendReasons;
    });

    this.store
      .select(selectEAuthCustomers)
      .pipe(takeUntil(this.destroy$))
      .subscribe(eAuthCustomers => this.eAuthCustomers = eAuthCustomers);

    this.store.select(selectHasAccessInitiateMoneyMovement)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.hasMmInitiateAccess = response;
      });

    this.store.select(selectStagedTransactionForm).pipe(takeUntil(this.destroy$)).subscribe(transactionForm => {
      let checkExtraPrompts = transactionForm?.extraPrompts as ChecksExtraPrompts;
      this.isCheckBsloa = checkExtraPrompts?.useStandingAuth === 'bundled' && transactionForm?.transactionType === transactionTypes.check;
    });


    this.viewDetails$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(viewDetails => {
      this.selectedEauthCustomers = [];
      this.caseId = viewDetails?.caseId ? viewDetails?.caseId.trim() : '';
      this.isPendingAuthorization = viewDetails?.isPendingAuthorization;
      this.transactionType = viewDetails.transactionType;
      this.accountselected = viewDetails.eAuthStatus?.customerList;
      this.status = this.statusMessage(viewDetails.status, viewDetails.eAuthStatus?.isEAuth, this.accountselected, viewDetails);
      this.source = viewDetails?.auditFields?.request?.source;
      this.initiatedBy = this.determineInitiatedBy(viewDetails);
      this.isLastModified = viewDetails?.auditFields?.isUpdated;
      this.canDisplayLastModifiedBy = this.setCanDisplayLastModifiedBy(viewDetails, this.status, this.transactionType);
      this.lastModifiedBy = this.canDisplayLastModifiedBy ? viewDetails.auditFields.update.user : '';
      this.lastModified = `${this.formatDatePipe.transform(viewDetails?.auditFields?.update?.date)} ${this.formatTime(viewDetails?.auditFields?.update?.time)}`;
      this.lastModificationSource = viewDetails?.auditFields?.update?.source;
      this.releasedBy = viewDetails?.releasedBy ?? '';
      // Released on
      if (this.checkReleasedDateIsValid(viewDetails)) {
        let releasedDateTime = convertUTCToEasternTime24(viewDetails?.releasedDate) ?? '';
        let releasedTime = this.formatTime(releasedDateTime.split(' ').slice(1).join(' ')) ?? '';
        this.releasedDate = `${releasedDateTime.split(' ')[0]} ${releasedTime} ${'ET'}`;
      }
      if (this.hasMmInitiateAccess && this.releasedBy) {
        // Mapping Initiated Date/Time on the "Submitted on:" field.Reference SAC-359416 
          if (this.checkInitiatedDateIsValid(viewDetails)) {
            let initiatedDateTime = convertUTCToEasternTime24(viewDetails?.initiatedDate ?? '');
            let initiatedTime = this.formatTime(initiatedDateTime.split(' ').slice(1).join(' ')) ?? '';
            this.submittedOn = `${initiatedDateTime.split(' ')[0]} ${initiatedTime} ${'ET'}`;
          }
          // when created in CC but in I&R firm, show the regular date
          else if (viewDetails?.auditFields?.request?.source === 'Schwab Internal') {
            this.submittedOn = `${this.formatDatePipe.transform(viewDetails?.auditFields?.request?.date)} ${this.formatTime(viewDetails?.auditFields?.request?.time ?? '')}`;
          }
      } 
      else {
        this.submittedOn = `${this.formatDatePipe.transform(viewDetails?.auditFields?.request?.date)} ${this.formatTime(viewDetails?.auditFields?.request?.time ?? '')}`;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  setCanDisplayLastModifiedBy(viewDetails: ViewDetails, status: string, transactionType: string): boolean {
    if (status === 'Cancelled' && transactionType === "wire") {
      return false;
    }
    return viewDetails?.auditFields?.update?.user !== undefined && viewDetails?.auditFields?.update?.user?.trim() !== '';
  }

  checkReleasedDateIsValid(viewDetails: ViewDetails): boolean {
    return !!viewDetails?.releasedDate?.trim() && viewDetails?.releasedDate?.trim() != '0001-01-01T00:00:00';
  }

  checkInitiatedDateIsValid(viewDetails: ViewDetails): boolean {
    return !!viewDetails?.initiatedDate?.trim() && viewDetails?.initiatedDate?.trim() != '0001-01-01T00:00:00';
  }

  statusMessage(transactionStatus: CodeAndDescription,isEAuth: boolean | undefined,customerList: EAuthStatusMessageFields[] | undefined,viewDetails: ViewDetails ): string {

    if (this.transactionType === transactionTypes.ach && this.pendReasons.includes(this.pendReasonStrings.schwabReview)) {
      return `Your transaction was received and is in process. You can view its status in the Account Transactions table in Move Money.`;
    }

    //if check, pend reason is ndfa/lpfa and use standingAuth is bundled return pending
    let isStandingAuth = viewDetails?.type === 'standing-auth';
    if (this.transactionType === transactionTypes.check && this.pendReasons.includes(this.pendReasonStrings.ndfaLPFA) && this.isCheckBsloa && !isStandingAuth) {
      return 'Pending';
    }

    let statusMessage = `${transactionStatus.description}`;
    if (isEAuth && customerList && customerList.length !== 0) {
      let customerCount = customerList.length;
      let count = 1;
      return this.formatCustomerList(customerList, statusMessage, count, customerCount);
    }
    let isPendingLoa = this.isPendingLoa(this.pendReasons);
    switch (transactionStatus.description) {
      case 'Pending': {
        if (isPendingLoa) {
          return `${statusMessage} - Awaiting signed authorization`
        } else if (isEAuth) {
          return `${statusMessage} - Awaiting electronic authorization`
        } else if (this.transactionType === transactionTypes.check && this.isCheckBsloa && !this.pendReasons?.length) {
          return statusMessage;
        }
        return `${statusMessage} - Request received. Schwab is reviewing.`
      }
      case 'Pending-Review': {
        if (isPendingLoa) {
          return `Pending - Awaiting signed authorization`
        } else if (isEAuth) {
          return `Pending - Awaiting electronic authorization`
        }
        return `Pending - Request received. Schwab is reviewing.`
      }
      default: {
        return statusMessage;
      }
    }
  }

  isPendingLoa(pendReasons: string[]): boolean {
    return pendReasons.includes(this.pendReasonStrings.clientAuthLOA) || pendReasons.includes(this.pendReasonStrings.ndfaLPFA) || pendReasons.includes(this.pendReasonStrings.taxElections);
  }

  formatCustomerList(customerList: EAuthStatusMessageFields[], status: string, count: number, length: number) {
    status += ' - ';
    customerList.forEach(customer => {
      let custEauthStatus = customer.customerEauthStatus ?? customer.loginIdEnrollStatus;
      let eAuthMessage = '';
      if (custEauthStatus === 'Decline') {
        eAuthMessage = eAuthStatusStrings.Declined;
      } else if (custEauthStatus === 'Accept' || custEauthStatus === 'Complete') {
        eAuthMessage = eAuthStatusStrings.Approved;
      } else if (custEauthStatus === 'Pending') {
        eAuthMessage = eAuthStatusStrings.Pending;
      } else if (custEauthStatus === 'Expired') {
        eAuthMessage = eAuthStatusStrings.Expired;
      } else {
        eAuthMessage = eAuthStatusStrings.Pending;
      }
      status += `${eAuthMessage} ${this.namePipe.transform(customer?.firstName)} ${this.namePipe.transform(customer?.lastName)} at ${customer.emailId}`
      if (count < length && length != 2) {
        status += `,\n`;
      }
      if (count === length - 1) {
        status += ` and\n`;
      }
      count++;
    });
    return status;
  }

  formatTime(timestamp: string): string {
    if (!timestamp) {
      return '';
    }
    if (timestamp[0] === '0') {
      timestamp = timestamp.slice(1);
    }
    const timeArr: string[] = timestamp.split(' ');
    if (timeArr.length === 1 && timeArr[0] === '') {
      return '';
    }
    let amPm: string = timeArr[1];
    timeArr[1] = this.formatTimePipe.transform(amPm);
    return timeArr.join(' ');
  }

  determineInitiatedBy(viewDetails: ViewDetails): string {
    if (viewDetails?.auditFields?.request?.user?.trim()) {
      return viewDetails.auditFields.request.user;
    }
    else if (viewDetails?.auditFields?.request?.source === sourceOfLastModification.SchwabInternal){
      return sourceOfLastModification.SchwabInternal;
    }
    else {
      return '';
    }
  }

  get condensedViewHasSecondColumn(): boolean {
    return this.useCondensedLayout 
      && (
        // be sure to add any new column 2 fields here to accurately determine if there is a second column
        (!!this.releasedDate?.trim() && this.hasMmInitiateAccess) 
        || (!!this.releasedBy?.trim() && this.hasMmInitiateAccess) 
        || this.canDisplayLastModifiedBy
        || this.isLastModified);
  }
}
